import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import '../../css/paginacion.css';

/**
 * Paginación única del dashboard: Anterior, números con saltos (1 … 4 5 6 … 12),
 * Siguiente y el rango que se está viendo. Todas las listas paginadas la usan
 * para que se vea y se maneje igual en cualquier pantalla.
 *
 * @param pagina        página actual (empieza en 1)
 * @param porPagina     elementos por página, para calcular el rango
 * @param total         elementos totales tras filtrar
 * @param onCambiar     recibe el número de página elegido
 * @param etiqueta      nombre accesible del <nav> (p. ej. "Paginación de usuarios")
 * @param compacta      menos aire, para cuando va dentro de una tarjeta
 */
const Paginacion = ({ pagina, porPagina, total, onCambiar, etiqueta, compacta = false }) => {
  const { t } = useTranslation();
  const totalPaginas = Math.max(1, Math.ceil(total / porPagina));
  const actual = Math.min(Math.max(pagina, 1), totalPaginas);

  // Primera, última y las vecinas de la actual; el resto se resume con "…"
  const paginasVisibles = useMemo(() => {
    if (totalPaginas <= 1) return [];
    const cercanas = new Set([1, totalPaginas, actual, actual - 1, actual + 1]);
    const ordenadas = [...cercanas].filter((n) => n >= 1 && n <= totalPaginas).sort((a, b) => a - b);
    return ordenadas.reduce((acumulado, numero, indice) => {
      if (indice && numero - ordenadas[indice - 1] > 1) acumulado.push('hueco');
      acumulado.push(numero);
      return acumulado;
    }, []);
  }, [actual, totalPaginas]);

  if (!total) return null;

  const ir = (numero) => {
    if (numero >= 1 && numero <= totalPaginas && numero !== actual) onCambiar(numero);
  };

  const desde = (actual - 1) * porPagina + 1;
  const hasta = Math.min(actual * porPagina, total);

  return (
    <nav className={`pgn${compacta ? ' pgn--compacta' : ''}`} aria-label={etiqueta || t('comun.paginacion.etiqueta')}>
      <div className="pgn-controles">
        <button type="button" className="pgn-nav" onClick={() => ir(actual - 1)} disabled={actual <= 1}>
          <FiChevronLeft aria-hidden="true" /><span>{t('comun.paginacion.anterior')}</span>
        </button>
        {paginasVisibles.length > 0 && (
          <ul className="pgn-numeros">
            {paginasVisibles.map((entrada, indice) => (
              <li key={entrada === 'hueco' ? `hueco-${indice}` : entrada}>
                {entrada === 'hueco' ? (
                  <span className="pgn-hueco" aria-hidden="true">…</span>
                ) : (
                  <button
                    type="button"
                    className={`pgn-numero${entrada === actual ? ' activa' : ''}`}
                    aria-current={entrada === actual ? 'page' : undefined}
                    aria-label={t('comun.paginacion.irAPagina', { n: entrada })}
                    onClick={() => ir(entrada)}
                  >
                    {entrada}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        <button type="button" className="pgn-nav" onClick={() => ir(actual + 1)} disabled={actual >= totalPaginas}>
          <span>{t('comun.paginacion.siguiente')}</span><FiChevronRight aria-hidden="true" />
        </button>
      </div>
      <p className="pgn-resumen">{t('comun.paginacion.mostrando', { desde, hasta, total })}</p>
    </nav>
  );
};

export default Paginacion;
